import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";

export type CallNoteStatus = "idle" | "saving" | "saved" | "error";

export type CallNoteDraft = {
  text: string;
  status: CallNoteStatus;
};

type CallNotesState = {
  // Keyed by the backend Call.id. Lives here rather than in the workbench so
  // folding the pop to its bar, or closing the phone, never loses what was
  // typed (ENG-2226).
  drafts: Record<number, CallNoteDraft>;
};

const initialState: CallNotesState = { drafts: {} };

export const callNotesSlice = createSlice({
  name: "callNotes",
  initialState,
  reducers: {
    noteDraftChanged: (
      state,
      action: PayloadAction<{ callId: number; text: string }>
    ) => {
      const { callId, text } = action.payload;
      state.drafts[callId] = { text, status: "idle" };
    },
    noteSaveStarted: (state, action: PayloadAction<{ callId: number }>) => {
      const draft = state.drafts[action.payload.callId];
      if (draft) draft.status = "saving";
    },
    noteSaved: (
      state,
      action: PayloadAction<{ callId: number; text: string }>
    ) => {
      const { callId, text } = action.payload;
      const draft = state.drafts[callId];
      // A keystroke that landed while the save was in flight keeps "idle", so
      // the debounce saves it next.
      if (draft && draft.text === text) draft.status = "saved";
    },
    noteSaveFailed: (state, action: PayloadAction<{ callId: number }>) => {
      const draft = state.drafts[action.payload.callId];
      if (draft) draft.status = "error";
    },
    noteDraftDropped: (state, action: PayloadAction<{ callId: number }>) => {
      delete state.drafts[action.payload.callId];
    }
  }
});

export const {
  noteDraftChanged,
  noteSaveStarted,
  noteSaved,
  noteSaveFailed,
  noteDraftDropped
} = callNotesSlice.actions;

export const selectCallNoteDraft = (state: RootState, callId: number | null) =>
  callId === null ? undefined : state.callNotes.drafts[callId];

export default callNotesSlice.reducer;
